/**
 * FlahaSOIL v2 — Salinity and Sodicity diagnostics (dark-native).
 */
import { Box, Stack, Typography, LinearProgress, Chip } from "@mui/material";
import type { ScientificChartData } from "../types/ScientificChartTypes";
import type { SalinitySodicityChartData } from "../adapters/salinitySodicityChartAdapter";
import { chartTheme, severityColor } from "../utils/chartTheme";

const SEVERITY_INDEX: Record<string, number> = {
  None: 0,
  Slight: 25,
  Moderate: 50,
  Strong: 75,
  Severe: 100,
};

interface SalinitySodicityChartProps {
  chartData: ScientificChartData<SalinitySodicityChartData>;
}

export function SalinitySodicityChart({ chartData }: SalinitySodicityChartProps) {
  const { data, accessibilitySummary, units } = chartData;

  return (
    <Stack spacing={2} aria-label={accessibilitySummary}>
      <SeverityGauge
        label="Salinity Severity"
        severity={data.salinitySeverity || null}
        value={data.ec}
        unit={units.ec || ""}
      />
      <SeverityGauge
        label="Sodicity Severity"
        severity={data.sodicitySeverity || null}
        value={data.esp || data.sar}
        unit={(data.esp ? units.esp : units.sar) || ""}
      />

      {data.ec !== null && data.clay !== null && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Salinity hazard (EC vs clay %)
          </Typography>
          <Box
            sx={{
              height: 120,
              position: "relative",
              border: `1px solid ${chartTheme.plotBorder}`,
              mt: 1,
              borderRadius: 1,
              backgroundColor: chartTheme.plotBg,
              backgroundImage: `
                linear-gradient(${chartTheme.grid} 1px, transparent 1px),
                linear-gradient(90deg, ${chartTheme.grid} 1px, transparent 1px)
              `,
              backgroundSize: "20% 25%",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: `${Math.min(100, (data.ec / 20) * 100)}%`,
                top: `${100 - (data.clay || 0)}%`,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: chartTheme.seriesPrimary,
                boxShadow: `0 0 12px ${chartTheme.seriesPrimaryGlow}`,
                border: `1.5px solid ${chartTheme.pointStroke}`,
                transform: "translate(-50%, -50%)",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 4,
                right: 6,
                color: "workspace.textMuted",
              }}
            >
              EC: {data.ec.toFixed(1)} {units.ec}
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
}

function SeverityGauge({
  label,
  severity,
  value,
  unit,
}: {
  label: string;
  severity: string | null;
  value: number | null;
  unit: string;
}) {
  const val = severity ? SEVERITY_INDEX[severity] ?? 0 : 0;
  const color = severityColor(severity);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2">{label}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {value !== null && (
            <Typography variant="caption">
              {value.toFixed(1)} {unit}
            </Typography>
          )}
          <Chip
            size="small"
            label={severity || "Unknown"}
            sx={{
              backgroundColor: color,
              color: chartTheme.pointStroke,
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 700,
            }}
          />
        </Stack>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={val}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: chartTheme.barTrack,
          "& .MuiLinearProgress-bar": { backgroundColor: color },
        }}
      />
    </Box>
  );
}
